import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import { fetchAdminPermissions } from '../../api'
import type { AdminPermission } from '../../types'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const ROLE_BADGE_COLORS: Record<string, string> = {
  'super-admin': 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-800',
  'business-owner': 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800',
  agent: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
}

function ResourceGroup({
  resource,
  perms,
  t,
}: {
  resource: string
  perms: AdminPermission[]
  t: (key: string) => string
}) {
  const [open, setOpen] = useState(true)

  return (
    <div className="rounded-lg border bg-card">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium capitalize">{resource}</span>
          <Badge variant="secondary" className="text-xs">
            {perms.length}
          </Badge>
        </div>
        <ChevronDown
          className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="divide-y border-t px-4">
          {perms.map((perm) => (
            <div
              key={perm.permission_id}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="flex flex-col gap-0.5">
                <code className="text-xs font-semibold text-foreground">{perm.permission_id}</code>
                <p className="text-xs text-muted-foreground">
                  {(t as (key: string, fallback: string) => string)(`admin.permissions.descriptions.${perm.resource}.${perm.action}`, perm.description)}
                </p>
              </div>
              <div className="flex flex-wrap gap-1 sm:ml-4 sm:shrink-0">
                {perm.roles.length === 0 ? (
                  <span className="text-xs text-muted-foreground">
                    {t('admin.permissions.noRoles')}
                  </span>
                ) : (
                  perm.roles.map((role) => (
                    <Badge
                      key={role.slug}
                      variant="outline"
                      className={cn('rounded-full text-xs', ROLE_BADGE_COLORS[role.slug])}
                    >
                      {role.name}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function PermissionsTab() {
  const { t } = useTranslation()

  const permissionsQuery = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: fetchAdminPermissions,
  })

  const groups = permissionsQuery.data?.data ?? {}
  const groupEntries = Object.entries(groups)

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('admin.permissions.description')}</p>

      {permissionsQuery.isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {groupEntries.map(([resource, perms]) => (
            <ResourceGroup key={resource} resource={resource} perms={perms} t={t} />
          ))}
        </div>
      )}
    </div>
  )
}
